import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  STARTUP_FEEDBACK_REASON_LABELS,
  STARTUP_FEEDBACK_REASON_CODES,
} from '../../constants/startupFeedback';
import type { StartupFeedbackReasonCode } from '../../types/firestore';

const PREDEFINED_REASONS = STARTUP_FEEDBACK_REASON_CODES.filter(
  (code): code is StartupFeedbackReasonCode => code !== 'other'
);

export interface RemoveFavoriteFeedbackPayload {
  reasons: StartupFeedbackReasonCode[];
  otherReason: string | null;
}

interface RemoveFavoritePopupProps {
  isOpen: boolean;
  startupName: string;
  /** True when this investor already left feedback for this startup. */
  alreadySubmitted?: boolean;
  onKeep: () => void;
  onRemove: (payload: RemoveFavoriteFeedbackPayload | null) => void | Promise<void>;
  isSending?: boolean;
}

const RemoveFavoritePopup: React.FC<RemoveFavoritePopupProps> = ({
  isOpen,
  startupName,
  alreadySubmitted = false,
  onKeep,
  onRemove,
  isSending = false,
}) => {
  const [selectedReasons, setSelectedReasons] = useState<Set<StartupFeedbackReasonCode>>(new Set());
  const [otherChecked, setOtherChecked] = useState(false);
  const [otherText, setOtherText] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelectedReasons(new Set());
    setOtherChecked(false);
    setOtherText('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSending) onKeep();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSending, onKeep]);

  const toggleReason = useCallback((code: StartupFeedbackReasonCode) => {
    setSelectedReasons(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const canRemove = alreadySubmitted || selectedReasons.size > 0 || otherChecked;

  const handleRemove = async () => {
    if (!canRemove || isSending) return;
    try {
      const hasNewFeedback = selectedReasons.size > 0 || otherChecked;
      if (alreadySubmitted && !hasNewFeedback) {
        await Promise.resolve(onRemove(null));
        onKeep();
        return;
      }
      const reasons: StartupFeedbackReasonCode[] = [
        ...Array.from(selectedReasons),
        ...(otherChecked ? (['other'] as const) : []),
      ];
      const otherReason = otherChecked ? otherText.trim() || null : null;
      await Promise.resolve(onRemove({ reasons, otherReason }));
      onKeep();
    } catch (error) {
      console.error('Error removing favorite:', error);
      onKeep();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSending) onKeep();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100000] p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-favorite-popup-title"
    >
      <div
        className="border border-gray-700 rounded-2xl shadow-xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#1E1E1C', fontFamily: 'Rubik, sans-serif' }}
        onClick={e => e.stopPropagation()}
      >
        <h2
          id="remove-favorite-popup-title"
          className="text-white text-lg font-semibold text-center mb-2"
        >
          Unfollow {startupName}?
        </h2>
        <p className="text-gray-300 text-sm text-center mb-5">
          You will no longer be able to contact them or track their progress.
        </p>

        <p className="text-white text-sm font-semibold text-left mb-4">
          Help improve the <strong>quality of the startups</strong> by providing feedback{' '}
          <span className="underline">directly to the Founder.</span>
        </p>

        <div className="space-y-3 mb-5">
          {PREDEFINED_REASONS.map(code => (
            <label
              key={code}
              className="flex items-start gap-3 cursor-pointer group text-left text-sm"
            >
              <input
                type="checkbox"
                checked={selectedReasons.has(code)}
                onChange={() => toggleReason(code)}
                className="mt-1 w-4 h-4 rounded border-gray-500 bg-gray-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
              />
              <span className="text-gray-200 group-hover:text-white">
                {STARTUP_FEEDBACK_REASON_LABELS[code]}
              </span>
            </label>
          ))}

          <div className="pt-2">
            <label className="flex items-start gap-3 cursor-pointer group text-left text-sm">
              <input
                type="checkbox"
                checked={otherChecked}
                onChange={() => setOtherChecked(!otherChecked)}
                className="mt-1 w-4 h-4 rounded border-gray-500 bg-gray-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
              />
              <span className="text-gray-200 group-hover:text-white">
                {STARTUP_FEEDBACK_REASON_LABELS.other}:
              </span>
            </label>
            <input
              type="text"
              value={otherText}
              onChange={e => setOtherText(e.target.value)}
              disabled={!otherChecked}
              className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={onKeep}
            disabled={isSending}
            className="w-full px-5 py-2.5 font-medium rounded-lg border-2 border-emerald-700 text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2 focus:ring-offset-[#1E1E1C] transition-colors disabled:opacity-50"
          >
            Oops, <span className="font-bold">keep this startup</span> as a favorite!
          </button>
          <button
            type="button"
            onClick={handleRemove}
            disabled={!canRemove || isSending}
            className="w-full px-5 py-2.5 font-medium rounded-lg border-2 border-red-500 text-white hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-[#1E1E1C] transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
          >
            {isSending ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 align-middle" />
                Removing...
              </>
            ) : (
              'Unfollow'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RemoveFavoritePopup;
